import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Row } from 'src/shared/components/Semantic/all'
import { Image, Button } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import {
  buildAppRequestPath,
  buildLoginSignupPath,
  isGuidedAssistantPath,
  paths,
} from 'src/routes/paths'
import { LanguageSelector } from 'src/shared/components/LangCurrencySelector'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import { Menu } from 'lucide-react'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { MobileMenu } from '../components/MobileMenu'
import { UserProfileButton } from '../components/UserProfileButton'
import { NavItems } from './components/NavItems'
import { Navbar } from './components/Navbar'
import type { NavItem } from './types'

interface TopNavBarProps {
  navItems: NavItem[]
  variant?: 'light' | 'dark'
  overlayNav?: boolean
}

const LogoSection = ({
  onLogoClick,
  variant = 'light',
}: {
  onLogoClick: () => void
  variant?: 'light' | 'dark'
}) => (
  <Row className="items-center gap-1 z-10">
    <Image
      src={IMAGE_URLS.logos.klayder}
      alt="Klayder"
      className={cn(
        'h-7 object-cover mt-0.5 cursor-pointer',
        variant === 'dark' && 'brightness-0 invert'
      )}
      onClick={onLogoClick}
    />
    <LanguageSelector variant={variant} />
  </Row>
)

const LoggedOutActions = ({
  size = 'default',
  compact = false,
  showTalkToUs = true,
  onLogin,
  onSignup,
  onTalkToUs,
}: {
  size?: 'default' | 'sm' | 'tiny'
  compact?: boolean
  showTalkToUs?: boolean
  onLogin: () => void
  onSignup: () => void
  onTalkToUs: () => void
}) => {
  const { t } = useLocalization()
  return (
    <Row className={cn('items-center', compact ? 'gap-1' : 'gap-3')}>
      <Button
        size={size}
        variant="ghost"
        onClick={onLogin}
        className="px-2 font-medium"
      >
        {t('Log in', 'Logga in')}
      </Button>
      {compact || !showTalkToUs ? null : (
        <Button size={size} variant="secondary" onClick={onTalkToUs} className="px-3">
          {t('Talk to us', 'Prata med oss')}
        </Button>
      )}
      <Button
        size={size}
        variant="primaryOrange"
        onClick={onSignup}
        className="px-3"
      >
        {compact
          ? t('Sign up', 'Skapa konto')
          : t('Sign up for free', 'Registrera dig gratis')}
      </Button>
    </Row>
  )
}

export const TopNavBar = ({
  navItems,
  variant = 'light',
  overlayNav = false,
}: TopNavBarProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { public_id } = useParams<{ public_id: string }>()
  const { t } = useLocalization()
  const { session } = useAuth()
  const loggedIn = !!session?.user?.email

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  const onGuidedAssistant = isGuidedAssistantPath(location.pathname)
  const loginSignupNext =
    onGuidedAssistant && public_id
      ? buildAppRequestPath(public_id)
      : undefined

  const goToLogin = () =>
    navigate(buildLoginSignupPath(loginSignupNext, { login: true }))
  const goToSignup = () => navigate(buildLoginSignupPath(loginSignupNext))
  const goToContact = () => navigate(paths.contact)
  const goToAccount = () => navigate(paths.myAccount + paths.dashboard)

  return (
    <>
      <Navbar variant={variant} overlayNav={overlayNav}>
        {/* Desktop */}
        <div className="relative z-[60] mx-auto hidden w-full flex-row items-center justify-between px-5 py-2.5 lg:flex">
          <LogoSection
            onLogoClick={() => navigate(paths.home)}
            variant={variant}
          />

          <div
            className={cn(
              'absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium lg:flex',
              'text-zinc-600'
            )}
          >
            <NavItems
              items={navItems}
              onItemClick={navigate}
              variant={variant}
              className="!relative !inset-auto !flex-none"
            />
          </div>

          <Row className="relative items-center gap-3">
            {loggedIn ? (
              <>
                <Button
                  size="default"
                  variant="primaryOrange"
                  onClick={goToContact}
                  className="px-3"
                >
                  {t('Talk to us', 'Prata med oss')}
                </Button>
                <UserProfileButton
                  onClick={goToAccount}
                  colorVariant={variant}
                />
              </>
            ) : (
              <LoggedOutActions
                onLogin={goToLogin}
                onSignup={goToSignup}
                onTalkToUs={goToContact}
                showTalkToUs={!onGuidedAssistant}
              />
            )}
          </Row>
        </div>

        {/* Mobile header */}
        <div className="relative z-50 mx-auto flex w-full flex-col items-center justify-between px-0 py-2 lg:hidden">
          <div className="flex w-full flex-row items-center justify-between">
            <Row className="items-center gap-1 z-10 flex-1 min-w-0">
              <Image
                src={IMAGE_URLS.logos.klayder}
                alt="Klayder"
                className={cn(
                  'h-7 object-cover ml-2 mt-0.5 cursor-pointer flex-shrink-0',
                  variant === 'dark' && 'brightness-0 invert'
                )}
                onClick={() => navigate(paths.home)}
              />
              <LanguageSelector variant={variant} />
            </Row>

            <Row className="items-center gap-1 shrink-0">
              {loggedIn && (
                <UserProfileButton
                  onClick={goToAccount}
                  colorVariant={variant}
                  showName={false}
                />
              )}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  'hover:bg-gray-100 active:bg-gray-200'
                )}
                aria-label="Open navigation menu"
                aria-expanded={isMobileMenuOpen}
              >
                <Menu className={cn('h-5 w-5', 'text-gray-700')} />
              </button>
            </Row>
          </div>
        </div>
      </Navbar>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navItems={navItems}
        loggedIn={loggedIn}
        onLogin={goToLogin}
        onSignup={goToSignup}
        onItemClick={(href) => {
          navigate(href)
          setIsMobileMenuOpen(false)
        }}
      />
    </>
  )
}
